'use client';
import React from 'react';
import AppImage from '@/components/ui/AppImage';
import { MapPin, Star, Clock, Briefcase, Tag, Building2, User } from 'lucide-react';

interface SearchResult {
  id: string;
  type: 'classified' | 'job' | 'service' | 'business' | 'person';
  title: string;
  subtitle: string;
  meta: string;
  price?: string;
  rating?: number;
  image: string;
  alt: string;
  badge: string;
  badgeColor: string;
  timeAgo: string;
}

const allResults: SearchResult[] = [
{
  id: 'res-001', type: 'classified', title: 'iPhone 15 Pro Max 256GB Space Black',
  subtitle: 'Electronics · Phones', meta: 'Westlands, Nairobi', price: 'KES 165,000',
  rating: 4.9, badge: 'Classified', badgeColor: '#00BCD4', timeAgo: '2 hrs ago',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a2abb466-1773767818826.png",
  alt: 'Black iPhone 15 Pro Max on white background'
},
{
  id: 'res-002', type: 'job', title: 'Senior iOS Developer — Safaricom PLC',
  subtitle: 'Technology · Full-time', meta: 'Westlands, Nairobi', price: 'KES 180,000/mo',
  badge: 'Job', badgeColor: '#FFB300', timeAgo: '4 hrs ago',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19666f297-1764748931548.png",
  alt: 'Safaricom office building with company branding in Nairobi'
},
{
  id: 'res-003', type: 'service', title: 'Phone Repair — TechFix Nairobi',
  subtitle: 'Electronics Repair', meta: 'CBD, Nairobi', rating: 4.7,
  badge: 'Service', badgeColor: '#FF6D00', timeAgo: '1 day ago',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1606cc00e-1766584116793.png",
  alt: 'Technician repairing a smartphone screen in a workshop'
},
{
  id: 'res-004', type: 'classified', title: 'Samsung Galaxy S24+ 512GB Violet',
  subtitle: 'Electronics · Phones', meta: 'Kilimani, Nairobi', price: 'KES 118,000',
  rating: 4.6, badge: 'Classified', badgeColor: '#00BCD4', timeAgo: '5 hrs ago',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_190f3b871-1772624438215.png",
  alt: 'Samsung Galaxy S24 Plus in violet colour on a table'
},
{
  id: 'res-005', type: 'business', title: 'Jumia Kenya — Online Marketplace',
  subtitle: 'E-Commerce · Verified Business', meta: 'Nairobi CBD', rating: 4.4,
  badge: 'Business', badgeColor: '#7C4DFF', timeAgo: '2 days ago',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17a59efac-1767195851210.png",
  alt: 'Jumia Kenya delivery box and shopping bags on white background'
},
{
  id: 'res-006', type: 'person', title: 'Kevin Mutua — Freelance Developer',
  subtitle: 'Software Engineer · Available', meta: 'Remote / Nairobi', rating: 4.9,
  badge: 'Person', badgeColor: '#FF4081', timeAgo: '12 hrs ago',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_107b4af53-1772198985024.png",
  alt: 'Kevin Mutua profile photo, young Kenyan software developer'
}];


const typeIcons: Record<string, React.ElementType> = {
  classified: Tag,
  job: Briefcase,
  service: Building2,
  business: Building2,
  person: User
};

interface SearchResultsProps {
  query: string;
  category: string;
}

export default function SearchResults({ query, category }: SearchResultsProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          <span className="font-600 text-foreground">{allResults.length}</span> results for{' '}
          <span className="font-600" style={{ color: 'var(--primary)' }}>"{query}"</span>
        </p>
        <button className="text-xs font-600 px-3 py-1.5 rounded-xl transition-all duration-150 hover:bg-muted"
        style={{ color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}>
          Sort: Relevance
        </button>
      </div>

      {allResults.map((result) => {
        const TypeIcon = typeIcons[result.type] || Tag;
        return (
          <div key={result.id} className="search-result-card">
            <div className="flex gap-3">
              <AppImage src={result.image} alt={result.alt} width={72} height={72}
              className="w-18 h-18 rounded-xl object-cover flex-shrink-0" style={{ width: 72, height: 72 }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-600 text-foreground leading-tight line-clamp-2">{result.title}</p>
                  <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-700"
                  style={{ background: `${result.badgeColor}18`, color: result.badgeColor, border: `1px solid ${result.badgeColor}30` }}>
                    {result.badge}
                  </span>
                </div>
                <p className="text-[11px] mb-1.5" style={{ color: 'var(--muted-foreground)' }}>{result.subtitle}</p>
                <div className="flex items-center gap-3 flex-wrap">
                  {result.price &&
                  <span className="text-sm font-700 font-tabular" style={{ color: 'var(--primary)' }}>{result.price}</span>
                  }
                  {result.rating &&
                  <div className="flex items-center gap-1">
                      <Star size={11} fill="var(--accent)" style={{ color: 'var(--accent)' }} />
                      <span className="text-xs font-600 font-tabular text-foreground">{result.rating}</span>
                    </div>
                  }
                  <div className="flex items-center gap-1">
                    <MapPin size={11} style={{ color: 'var(--muted-foreground)' }} />
                    <span className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>{result.meta}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={11} style={{ color: 'var(--muted-foreground)' }} />
                    <span className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>{result.timeAgo}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>);

      })}
    </div>);

}